import type { QueryClient } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { MailPage } from "@/components/pages/mail/page"
import { mailContactsOptions } from "@/hooks/queries/mail-contacts"
import { reportsOptions } from "@/hooks/queries/reports"
import { useTabNavigation } from "@/hooks/use-tab-navigation"
import { DEFAULT_PAGE_SIZE } from "@/lib/constants"

const TABS = ["topics", "audiences"] as const
type MailTab = (typeof TABS)[number]

function isTab(value: unknown): value is MailTab {
	return (
		typeof value === "string" && (TABS as readonly string[]).includes(value)
	)
}

type TabPrefetcher = (qc: QueryClient) => void

const TAB_PREFETCHERS: Record<MailTab, TabPrefetcher> = {
	topics: (qc) => {
		qc.prefetchQuery(reportsOptions())
	},
	audiences: (qc) => {
		qc.prefetchQuery(
			mailContactsOptions({ page: 1, pageSize: DEFAULT_PAGE_SIZE }),
		)
	},
}

export const Route = createFileRoute("/_authenticated/mail/")({
	validateSearch: (
		search: Record<string, unknown>,
	): { tab?: MailTab; topic?: string } => ({
		tab: isTab(search.tab) ? search.tab : undefined,
		topic: typeof search.topic === "string" ? search.topic : undefined,
	}),
	loaderDeps: ({ search }) => ({ tab: search.tab }),
	loader: ({ context, deps }) => {
		TAB_PREFETCHERS[deps.tab ?? "topics"](context.queryClient)
	},
	component: MailRoute,
})

function MailRoute() {
	const { tab: tabSearch, topic } = Route.useSearch()
	const navigate = Route.useNavigate()
	const { tab, onTabChange } = useTabNavigation(navigate, "topics", tabSearch)
	return <MailPage tab={tab} onTabChange={onTabChange} initialTopic={topic} />
}
